import {getEntitySpanInsertValue, BOOL_ENTITY_TYPE, boolEntityOmitsContractText} from "./workWithEntities";
import {getEntityPatterns} from "./emptyEntities";
import { forEachSelectedObjectInnerField } from "./workWithObjectEntity";
import {findNextRawEntityMarker} from "./entityMarkerInHtml";
import {signerSlotsForBody} from "./additionalSignerSlots";

export const ENTITY_LABEL_BORDER_COLOR = "#DE9B94"

export function entityLabelStyle(color: string = ENTITY_LABEL_BORDER_COLOR): string {
    return `font-weight: bold; border: 1px solid ${color}; border-radius: 5px; page-break-inside: avoid; white-space: pre-wrap;`
}

export function isEntityLabelName(name: string | null | undefined): boolean {
    if (!name) {
        return false
    }
    return name.startsWith("<<") || name.startsWith("{{") || name.startsWith("&lt;&lt;")
}

/** Курсор внутри уже закрытого тега метки не должен считать keyword «ещё не обёрнутым». */
export function isInsideUnclosedEntityLabel(beforeText: string): boolean {
    const re = /<(span|div)\b[^>]*\bname="(<<|&lt;&lt;|\{\{)/gi
    let last: RegExpExecArray | null = null
    let match: RegExpExecArray | null
    while ((match = re.exec(beforeText))) {
        last = match
    }
    if (!last) {
        return false
    }
    const tag = last[1]
    const after = beforeText.slice(last.index)
    return after.indexOf(`</${tag}>`) === -1
}

const ENTITY_LABEL_OPEN_TAG_RE = /<(span|div)(\s[^>]*\bname="(?:<<[^"]*>>|&lt;&lt;[^"]*&gt;&gt;)"[^>]*)>/gi

/** Старые метки без contenteditable=false — дописываем, чтобы курсор не застревал внутри. */
export function ensureEntityLabelsNonEditable(html: string): string {
    if (!html) {
        return html
    }
    return html.replace(ENTITY_LABEL_OPEN_TAG_RE, (_full, tag: string, attrs: string) => {
        if (/\bcontenteditable\s*=/i.test(attrs)) {
            const patched = attrs.replace(
                /\bcontenteditable\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/i,
                'contenteditable="false"',
            )
            return `<${tag}${patched}>`
        }
        return `<${tag} contenteditable="false"${attrs}>`
    })
}

export function entityLabelOpenTag(tag: string, name: string, color: string = ENTITY_LABEL_BORDER_COLOR): string {
    return `<${tag} contenteditable="false" style="${entityLabelStyle(color)}" name="${name}">`
}

export function addSpanTagsToBody(template: any, body: string, color: string = ENTITY_LABEL_BORDER_COLOR): string {
    const entities = template?.entities ?? []
    entities.forEach((entity: any) => {
        body = addSpanTagToString(entity, body, color, entities)
    })
    forEachSelectedObjectInnerField(entities, (objectEntity, innerEntity, fullKeyword) => {
        const fullEntity = {
            ...innerEntity,
            keyword: fullKeyword,
            value: innerEntity.value
        }
        body = addSpanTagToString(fullEntity, body, color, entities)
    })
    const systemEntities = template?.system_entities ?? []
    systemEntities.forEach((entity: any) => {
        body = addSpanTagToString(entity, body, color)
    })
    const additionalSigners = signerSlotsForBody(template)
    const signerCount = Math.max(
        additionalSigners.length,
        Number(template.settings?.additional_signers_count) || 0,
    )
    for (let i = 0; i < signerCount; i++) {
        const slot = additionalSigners[i]
        const slotEntities = Array.isArray(slot?.system_entities) ? slot.system_entities : []
        const seen = new Set<string>()
        const wrapSignerEntity = (entity: any) => {
            const key = entity?.keyword
            if (!key || seen.has(key)) {
                return
            }
            seen.add(key)
            const entityWithFullKeyword = {...entity, keyword: `Подписант ${i + 1}.${key}`}
            body = addSpanTagToString(entityWithFullKeyword, body, color)
        }
        slotEntities.forEach(wrapSignerEntity)
        systemEntities.forEach(wrapSignerEntity)
    }
    return ensureEntityLabelsNonEditable(body)
}

export function addSpanTagToString(entity: any, body: string, color: string = ENTITY_LABEL_BORDER_COLOR, otherEntities: any[] = []): string {
    if (!entity) {
        return body
    }
    
    let {normalPattern} = getEntityPatterns(entity.keyword)
    let name = normalPattern
    let tag = entity.type == "Фото" ? "div" : "span"
    const openTag = entityLabelOpenTag(tag, name, color)
    const closeTag = `</${tag}>`

    let offset = 0
    let found = findNextRawEntityMarker(body.toString(), entity.keyword, offset)

    while (found) {
        let beforeText = body.slice(Math.max(0, found.index - 3000), found.index)
        if (isInsideUnclosedEntityLabel(beforeText)) {
            offset = found.index + found.length
            found = findNextRawEntityMarker(body, entity.keyword, offset)
            continue
        }
        
        let prefix = body.slice(0, found.index)
        let valueToInsert = getEntitySpanInsertValue(entity, otherEntities)
        if (!valueToInsert && !(entity?.type === BOOL_ENTITY_TYPE && boolEntityOmitsContractText(entity))) {
            valueToInsert = `{{${entity.keyword}}}`
        }
        let suffix = body.slice(found.index + found.length)

        const wrapped = ` ${openTag}${valueToInsert}${closeTag} `
        body = prefix + wrapped + suffix
        
        offset = found.index + wrapped.length
        found = findNextRawEntityMarker(body, entity.keyword, offset)
    }
    
    return body
}
